import "./EmqPartStepper.scss";

export type EmqPartStepStatus =
  | "pending"
  | "current"
  | "answered"
  | "incorrect"
  | "skipped";

type EmqPartStepperProps = {
  partOrder: number;
  partsCount: number;
  showHint?: boolean;
  /** Overrides the default answering hint when set. */
  hint?: string;
  /** Soft highlight used when jumping back for post-submit review. */
  emphasize?: boolean;
  /** Status for each part 1..partsCount; falls back to position-based complete/current. */
  partStatuses?: EmqPartStepStatus[];
  /** When set, chips are clickable (e.g. post-submit review). */
  onPartClick?: (partOrder: number) => void;
};

function partLetter(partOrder: number): string {
  if (partOrder < 1) return "?";
  let n = partOrder;
  let label = "";
  while (n > 0) {
    n -= 1;
    label = String.fromCharCode(65 + (n % 26)) + label;
    n = Math.floor(n / 26);
  }
  return label;
}

export function EmqPartStepper({
  partOrder,
  partsCount,
  showHint = false,
  hint,
  emphasize = false,
  partStatuses,
  onPartClick,
}: EmqPartStepperProps) {
  if (partsCount < 1 || partOrder < 1) {
    return null;
  }

  const steps = Array.from({ length: partsCount }, (_, i) => i + 1);
  const hintText =
    hint ?? (showHint ? "Answer each part in turn" : undefined);
  const interactive = typeof onPartClick === "function";

  const statusFor = (step: number): EmqPartStepStatus => {
    const fromProp = partStatuses?.[step - 1];
    if (fromProp) return fromProp;
    if (step === partOrder) return "current";
    if (step < partOrder) return "answered";
    return "pending";
  };

  return (
    <div
      className={[
        "emq-part-stepper",
        emphasize ? "emq-part-stepper--emphasize" : "",
        interactive ? "emq-part-stepper--interactive" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="emq-part-stepper__bar">
        <span className="emq-part-stepper__badge">EMQ</span>

        <ol
          className="emq-part-stepper__steps"
          aria-label={`EMQ parts, part ${partOrder} of ${partsCount}`}
        >
          {steps.map((step, index) => {
            const status = statusFor(step);
            const isCurrent = step === partOrder;
            const chip = (
              <>
                {status === "answered" ? (
                  <svg
                    className="emq-part-stepper__check"
                    viewBox="0 0 12 12"
                    aria-hidden="true"
                  >
                    <path
                      d="M2.5 6.2 4.8 8.5 9.5 3.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : status === "incorrect" ? (
                  <span className="emq-part-stepper__cross" aria-hidden="true">
                    ×
                  </span>
                ) : (
                  partLetter(step)
                )}
              </>
            );

            return (
              <li
                key={step}
                className={[
                  "emq-part-stepper__step",
                  `emq-part-stepper__step--${status}`,
                  isCurrent ? "emq-part-stepper__step--selected" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {index > 0 ? (
                  <span
                    className="emq-part-stepper__connector"
                    aria-hidden="true"
                  />
                ) : null}
                {interactive ? (
                  <button
                    type="button"
                    className="emq-part-stepper__chip"
                    aria-current={isCurrent ? "step" : undefined}
                    aria-pressed={isCurrent}
                    onClick={() => onPartClick?.(step)}
                  >
                    {chip}
                  </button>
                ) : (
                  <span
                    className="emq-part-stepper__chip"
                    aria-current={isCurrent ? "step" : undefined}
                  >
                    {chip}
                  </span>
                )}
              </li>
            );
          })}
        </ol>

        <span className="emq-part-stepper__count" aria-hidden="true">
          {partOrder}/{partsCount}
        </span>
      </div>

      {hintText ? (
        <p className="emq-part-stepper__hint">{hintText}</p>
      ) : null}
    </div>
  );
}
